import React, { useCallback } from 'react';
import { Badge, Card } from 'react-bootstrap';
import { Position, Handle } from 'react-flow-renderer';
import { EmployeePosition } from './types';
import { getNodeDimensions, isPositionSelectable } from './utils';
import { NODE_SIZES } from './constants';

const PositionNodeContent: React.FC<{
  position: EmployeePosition;
  isSelected: boolean;
  displayStaffedPositions: boolean;
  t: (key: string, options?: { defaultValue?: string }) => string;
}> = ({ position, isSelected, displayStaffedPositions, t }) => {
  const isMinimal =
    !displayStaffedPositions &&
    position.flair__Staffed__c &&
    !position.isCurrentPosition;

  if (isMinimal) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100">
        <Badge
          bg="secondary"
          className="small px-1 py-1"
          style={{ fontSize: '0.5rem' }}>
          {t('statusStaffed')}
        </Badge>
      </div>
    );
  }

  return (
    <div className={position.isCurrentPosition ? 'p-2' : 'p-1'}>
      <div className="fw-bold small lh-sm">{position.Name}</div>
      {position.flair__Staffed__c && position.employment?.employee?.Name && (
        <div className="text-muted small">
          {position.employment.employee.Name}
        </div>
      )}
      <div className="mt-1">
        {position.isCurrentPosition ? (
          <Badge bg="warning" className="small">
            {t('statusCurrent')}
          </Badge>
        ) : (
          <Badge
            bg={position.flair__Staffed__c ? 'secondary' : 'success'}
            className="small">
            {position.flair__Staffed__c
              ? t('statusStaffed')
              : t('statusAvailable')}
          </Badge>
        )}
      </div>
    </div>
  );
};

export const PositionNodeComponent: React.FC<{
  data: {
    position: EmployeePosition;
    isSelected: boolean;
    onSelect: () => void;
    displayStaffedPositions: boolean;
    t: (key: string, options?: { defaultValue?: string }) => string;
  };
}> = ({
  data: { position, isSelected, onSelect, displayStaffedPositions, t },
}) => {
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!isPositionSelectable(position)) return;
      onSelect();
    },
    [onSelect, position],
  );

  const { width, height } = getNodeDimensions(
    position,
    displayStaffedPositions,
  );
  const isSelectable = isPositionSelectable(position);

  const getBackgroundClass = () => {
    if (position.isCurrentPosition) return 'position-node-current';
    if (isSelected) return 'position-node-selected';
    return 'position-node-default';
  };

  const getBodyPaddingClass = () => {
    if (width === NODE_SIZES.minimal.width)
      return 'position-node-body-minimal p-1';
    if (position.isCurrentPosition) return 'position-node-body-current p-3';
    return 'position-node-body-default p-2';
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        className="position-handle-hidden"
      />
      <Card
        className={`position-node position-node-container m-0 ${getBackgroundClass()} ${
          isSelected ? 'border-primary' : ''
        } ${position.isCurrentPosition ? 'border-warning' : ''} ${
          !isSelectable
            ? 'position-node-non-selectable opacity-75'
            : 'position-node-selectable'
        }`}
        style={{ width, height }}
        title={
          !isSelectable
            ? t('positionNotSelectable', {
                defaultValue:
                  'This position is already staffed and cannot be selected',
              })
            : ''
        }
        onClick={handleClick}>
        <Card.Body
          className={`text-center d-flex flex-column justify-content-center ${getBodyPaddingClass()}`}>
          <PositionNodeContent
            position={position}
            isSelected={isSelected}
            displayStaffedPositions={displayStaffedPositions}
            t={t}
          />
        </Card.Body>
      </Card>
      <Handle
        type="source"
        position={Position.Bottom}
        className="position-handle-bottom-hidden"
      />
    </>
  );
};
